import { useMediaQuery, useTheme } from '@mui/material';
import type { GridApiCommunity } from '@mui/x-data-grid/models/api/gridApiCommunity';
import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type RowPosition = Map<string, number>;

const getRowPositions = (gridElement: HTMLDivElement): RowPosition => {
  const positions = new Map<string, number>();

  Array.from(gridElement.querySelectorAll<HTMLElement>('.MuiDataGrid-row[data-id]')).forEach((row) => {
    if (row.dataset.id) positions.set(row.dataset.id, row.getBoundingClientRect().top);
  });

  return positions;
};

export const useDataGridRowReorderAnimation = (
  gridRef: RefObject<HTMLDivElement | null>,
  apiRef: RefObject<GridApiCommunity | null>
) => {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const positionsBeforeReorderRef = useRef<RowPosition>(new Map());
  const animationFrameRef = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [reorderVersion, setReorderVersion] = useState(0);
  const [animationVersion, setAnimationVersion] = useState(0);

  const captureRowPositions = useCallback(() => {
    if (prefersReducedMotion || !gridRef.current) return;

    positionsBeforeReorderRef.current = getRowPositions(gridRef.current);
    setReorderVersion((currentVersion) => currentVersion + 1);
  }, [gridRef, prefersReducedMotion]);

  useEffect(() => {
    if (reorderVersion === 0) return;

    apiRef.current?.applySorting();
    setAnimationVersion(reorderVersion);
  }, [apiRef, reorderVersion]);

  useLayoutEffect(() => {
    cleanupRef.current?.();

    if (prefersReducedMotion || positionsBeforeReorderRef.current.size === 0 || !gridRef.current) return undefined;

    const rows = Array.from(gridRef.current.querySelectorAll<HTMLElement>('.MuiDataGrid-row[data-id]'));
    const rowsToAnimate = rows
      .map((row) => ({ row, positionBeforeReorder: positionsBeforeReorderRef.current.get(row.dataset.id ?? '') }))
      .filter(
        ({ row, positionBeforeReorder }) =>
          positionBeforeReorder != null && positionBeforeReorder !== row.getBoundingClientRect().top
      );

    if (rowsToAnimate.length === 0) return undefined;

    rowsToAnimate.forEach(({ row, positionBeforeReorder }) => {
      const positionAfterReorder = row.getBoundingClientRect().top;
      row.style.transition = 'none';
      row.style.transform = `translateY(${positionBeforeReorder! - positionAfterReorder}px)`;
      row.style.willChange = 'transform';
    });

    const clearRowStyles = (row: HTMLElement) => {
      row.style.transition = '';
      row.style.transform = '';
      row.style.willChange = '';
    };

    animationFrameRef.current = requestAnimationFrame(() => {
      rowsToAnimate.forEach(({ row }) => {
        row.style.transition = theme.transitions.create('transform');
        row.style.transform = '';
      });
    });

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName !== 'transform' || !(event.currentTarget instanceof HTMLElement)) return;
      clearRowStyles(event.currentTarget);
    };

    rowsToAnimate.forEach(({ row }) => row.addEventListener('transitionend', handleTransitionEnd));

    const cleanup = () => {
      if (animationFrameRef.current != null) cancelAnimationFrame(animationFrameRef.current);
      rowsToAnimate.forEach(({ row }) => {
        row.removeEventListener('transitionend', handleTransitionEnd);
        clearRowStyles(row);
      });
      animationFrameRef.current = null;
    };

    cleanupRef.current = cleanup;
    return cleanup;
  }, [animationVersion, gridRef, prefersReducedMotion, theme.transitions]);

  useLayoutEffect(
    () => () => {
      cleanupRef.current?.();
    },
    []
  );

  return { captureRowPositions };
};
